import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCooldown } from '../../../utils/useCoolDown'
import { getPhoneAuthErrorMessage } from '../utils/phoneAuthErrors'

type Stage = 'phone' | 'code'

type UsePhoneCodeOptions<Result> = {
  sendCode: (phone: string | null) => Promise<unknown>
  verifyCode: (phone: string | null, code: string) => Promise<Result>
  onVerified: (result: Result, phone: string | null) => void
  onError?: (error: unknown) => void
}

export const usePhoneCode = <Result>({ sendCode, verifyCode, onVerified, onError }: UsePhoneCodeOptions<Result>) => {
  const { t } = useTranslation()
  const { cooldown, startCooldown } = useCooldown(60)
  const [stage, setStage] = useState<Stage>('phone')
  const [phone, setPhone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { mutate: send, isPending: sending } = useMutation({
    mutationFn: async (nextPhone: string | null) => {
      await sendCode(nextPhone)
      return nextPhone
    },
    onMutate: () => setError(null),
    onSuccess: (nextPhone) => {
      setPhone(nextPhone)
      setStage('code')
      startCooldown()
    },
    onError: (err) => {
      setError(getPhoneAuthErrorMessage(err, t))
      onError?.(err)
    },
  })

  const { mutate: verify, isPending: verifying } = useMutation({
    mutationFn: (code: string) => verifyCode(phone, code),
    onMutate: () => setError(null),
    onSuccess: (result) => onVerified(result, phone),
    onError: (err) => {
      setError(getPhoneAuthErrorMessage(err, t))
      onError?.(err)
    },
  })

  const resend = () => {
    if (cooldown > 0) return

    send(phone)
  }

  const changeNumber = () => {
    setError(null)
    setStage('phone')
  }

  return { stage, phone, error, cooldown, sending, verifying, send, verify, resend, changeNumber }
}
